'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot, Slottable } from '../slot';
import { CarouselRootContext } from './context/carousel-root.context';
import { useCarousel } from './hooks/use-carousel';
import { carouselRootStyles } from './styles/carousel-root.styles';
import type { CarouselRootPropsInterface } from './types/carousel-root.types';

/**
 * @since 10.0.0
 */
export const CarouselRoot = forwardRef<HTMLDivElement, CarouselRootPropsInterface>(
  (props: CarouselRootPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      role,
      children,
      className,
      css: cssProp,
      onNextClick,
      onPreviousClick,
      onScroll,
      onVisibleSlidesChange,
      scrollLeft,
      scrollToSlide,
      ...rest
    } = props;
    const styles = css(carouselRootStyles, cssProp);
    const Component = asChild ? Slot : 'section';

    const carousel = useCarousel({
      onNextClick,
      onPreviousClick,
      onScroll,
      onVisibleSlidesChange,
      scrollLeft,
      scrollToSlide,
    });

    const contextValue = useMemo(() => ({ carousel }), [carousel]);

    return (
      <CarouselRootContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          role={role}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Carousel.Root"
          className={cx('c11n', 'c11n_carousel__root', styles, className)}
          {...rest}
        >
          <Slottable>{children}</Slottable>
        </Component>
      </CarouselRootContext.Provider>
    );
  },
);

CarouselRoot.displayName = 'Carousel.Root';
